import { useEffect, useState } from "react";
import { formatCurrency } from "@/utils/formatCurrency.js";
import { paymentLabel } from "@/utils/onlinePurchase.js";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  ListGroup,
  Button,
  Image,
  Spinner,
  Alert,
} from "react-bootstrap";
import { formatDate } from "@/utils/formatDate";
import api from "@/api/api.js";
import Swal from "sweetalert2";
import { ROLES } from "@/config/roles.js";
import ConstanciaActions from "@/pages/shared/sections/ConstanciaActions.jsx";

const isImage = (filename) =>
  /\.(jpeg|jpg|gif|png|webp)$/i.test(filename ?? "");

const ShowTicket = () => {
  const { requestTicketId } = useParams();
  const navigate = useNavigate();

  const canApprove = (
    JSON.parse(sessionStorage.getItem("roles")) || []
  ).includes(ROLES.SUPER_ADMIN);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(null);

  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState(null);

  const [pdfFile, setPdfFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  const handleUploadInvoice = async () => {
    if (!pdfFile || !xmlFile || uploadingInvoice) return;
    setUploadingInvoice(true);
    try {
      const fd = new FormData();
      fd.append("pdf", pdfFile);
      fd.append("xml", xmlFile);
      await api.post(`/request_tickets/${ticket.id}/invoice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshTicket();
      setPdfFile(null);
      setXmlFile(null);
      Swal.fire({
        icon: "success",
        title: "Factura subida y notificada al usuario",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "No se pudo subir la factura",
        text: e?.response?.data?.error || "Intenta de nuevo.",
      });
    } finally {
      setUploadingInvoice(false);
    }
  };

  const refreshTicket = async () => {
    const { data } = await api.get(`/request_tickets/${requestTicketId}`);
    setTicket(data);
  };

  const submitApproval = async (approvalStatus) => {
    if (!ticket?.id) return;

    try {
      setApprovalLoading(true);
      setApprovalError(null);

      await api.patch(
        `/request_tickets/${ticket.id}/approval`,
        { approval_status: approvalStatus },
        {
          headers: {
            "Content-Type": "application/merge-patch+json",
            Accept: "application/json",
          },
        },
      );

      await refreshTicket();

      await Swal.fire({
        icon: "success",
        title: approvalStatus === 1 ? "Ticket aprobado" : "Ticket desaprobado",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error("Error approval", e);

      // Intenta tomar mensaje de API Platform / Symfony
      const message =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "No se pudo actualizar el estado de aprobación";

      setApprovalError(message);

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
    } finally {
      setApprovalLoading(false);
    }
  };

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const { data } = await api.get(`/request_tickets/${requestTicketId}`);
        setTicket(data);
        setError(null);
      } catch {
        setError("No se pudo cargar la solicitud");
        navigate("/request_tickets", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    if (requestTicketId) {
      fetchTicket();
    }
  }, [requestTicketId, navigate]);

  useEffect(() => {
    let objectUrl;

    const fetchImage = async () => {
      if (
        !ticket?.voucherOriginalName ||
        !isImage(ticket.voucherOriginalName)
      ) {
        setImageUrl(null);
        setImageError(null);
        return;
      }

      try {
        setImageError(null);

        const response = await api.get(`/request_tickets/${ticket.id}/image`, {
          responseType: "blob",
        });

        objectUrl = URL.createObjectURL(response.data);
        setImageUrl(objectUrl);
      } catch (e) {
        console.error("Error cargando la imagen del comprobante", e);
        setImageError("No se pudo cargar la imagen del comprobante");
        setImageUrl(null);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ticket?.id, ticket?.voucherOriginalName]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
        <p className="mt-2">Cargando detalles de la solicitud...</p>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <Alert variant="danger" className="mt-4">
        {error || "La solicitud no existe"}
      </Alert>
    );
  }

  return (
    <div className="container mt-4">
      <Button
        variant="outline-secondary"
        onClick={() => navigate(-1)}
        className="mb-3"
      >
        &larr; Volver
      </Button>

      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>Detalles de la solicitud</Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>ID:</strong> {ticket.id}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Paquete:</strong> {ticket.packageName}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Cantidad de folios:</strong> {ticket.quantityTickets}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Solicitado por:</strong>{" "}
                  {ticket.requestUser?.userProfile?.fullName}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Monto:</strong>{" "}
                  {ticket.priceCents == null
                    ? "-"
                    : formatCurrency(ticket.priceCents / 100)}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Pago:</strong> {paymentLabel(ticket)}
                  {ticket.paymentOrder?.paidAt && (
                    <> · {formatDate(ticket.paymentOrder.paidAt)}</>
                  )}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Estado:</strong>{" "}
                  {ticket.approvalStatus === 1
                    ? "Aprobado"
                    : ticket.approvalStatus === 0
                      ? "Rechazado"
                      : "Pendiente"}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Fecha de creación:</strong> {ticket.createdAtAgo}
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mt-3 mt-md-0">
          <Card>
            <Card.Header>Comprobante de pago</Card.Header>
            <Card.Body>
              {ticket.voucherPath ? (
                <>
                  <div className="mb-3">
                    <strong>Archivo:</strong> {ticket.voucherOriginalName}
                  </div>

                  {isImage(ticket.voucherOriginalName) ? (
                    <>
                      {imageError && (
                        <Alert variant="warning" className="mb-2">
                          {imageError}
                        </Alert>
                      )}

                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          fluid
                          rounded
                          className="mb-3"
                          style={{ maxHeight: "400px", objectFit: "contain" }}
                        />
                      ) : !imageError ? (
                        <div className="text-center text-muted">
                          Cargando imagen del comprobante...
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-center">
                      <Button
                        variant="primary"
                        href={ticket.voucherPath}
                        target="_blank"
                        download
                      >
                        Descargar comprobante
                      </Button>
                    </div>
                  )}

                  <div className="mt-3 small text-muted">
                    <strong>Hash de verificación:</strong> {ticket.voucherHash}
                  </div>
                </>
              ) : (
                <div className="text-center text-muted">
                  {ticket.paymentOrder
                    ? "Se pagó en línea: no hay comprobante que revisar."
                    : "No se encontró comprobante adjunto"}
                </div>
              )}
            </Card.Body>
            <Card.Footer>
              {ticket.paymentOrder && (
                <small className="text-muted">
                  Compra en línea: se aprueba sola cuando Conekta confirma el
                  pago.
                </small>
              )}
              {!ticket.paymentOrder &&
                ticket.approvalStatus !== 1 &&
                (canApprove ? (
                  <div className="mt-3">
                    {approvalError && (
                      <Alert variant="danger" className="mb-2">
                        {approvalError}
                      </Alert>
                    )}

                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        disabled={approvalLoading}
                        onClick={() => submitApproval(1)}
                      >
                        {approvalLoading ? "Procesando..." : "Aprobar"}
                      </Button>

                      <Button
                        variant="danger"
                        disabled={approvalLoading}
                        onClick={() => submitApproval(0)}
                      >
                        {approvalLoading ? "Procesando..." : "Desaprobar"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <small className="text-muted">
                    No tienes suficientes permisos para aprobar esa solicitud.
                  </small>
                ))}
            </Card.Footer>
          </Card>

          {ticket.approvedBy && (
            <Card className="mt-3">
              <Card.Header>Detalles de aprobación</Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>
                      {ticket.approvalStatus === 1 ? "Aprobado" : "No Aprobado"}{" "}
                      por:
                    </strong>{" "}
                    {ticket.approvedBy.userProfile.fullName}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Fecha de confirmación:</strong>{" "}
                    {formatDate(ticket.approvedDate)}
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>
          )}

          {ticket.approvalStatus === 1 && (
            <Card className="mt-3">
              <Card.Header>Subir factura del paquete</Card.Header>
              <Card.Body>
                {ticket.invoiceRequestedAt ? (
                  <Alert variant="info" className="mb-3">
                    <div>
                      <strong>Compañía a facturar:</strong>{" "}
                      {ticket.invoiceCompany?.name || "—"}
                    </div>
                    <div>
                      <strong>RFC:</strong>{" "}
                      {ticket.invoiceCompany?.taxIdentification || "—"}
                    </div>
                    <div>
                      <strong>Solicitada el:</strong>{" "}
                      {formatDate(ticket.invoiceRequestedAt)}
                    </div>
                    {ticket.invoiceCompany?.id && (
                      <>
                        <div className="mt-2">
                          <Link
                            to={`/${ticket.invoiceCompany.id}/detailCompany`}
                          >
                            <i className="bi bi-box-arrow-up-right me-1" />
                            Ver detalle fiscal de la compañía
                          </Link>
                        </div>
                        <div className="mt-2">
                          <ConstanciaActions
                            companyId={ticket.invoiceCompany.id}
                          />
                        </div>
                      </>
                    )}
                  </Alert>
                ) : (
                  <Alert variant="secondary" className="mb-3">
                    El usuario aún no solicita su factura.
                  </Alert>
                )}
                <p className="mb-3">
                  <strong>Cantidad a facturar:</strong> $
                  {ticket.invoiceAmount ?? "—"}
                </p>
                {ticket.invoiceOriginalName ? (
                  <Alert variant="success" className="mb-0">
                    Factura ya emitida y notificada al usuario.
                  </Alert>
                ) : (
                  <>
                    <div className="mb-2">
                      <label className="form-label">Factura (PDF)</label>
                      <input
                        type="file"
                        accept=".pdf"
                        className="form-control"
                        onChange={(e) =>
                          setPdfFile(e.target.files?.[0] || null)
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Factura (XML)</label>
                      <input
                        type="file"
                        accept=".xml"
                        className="form-control"
                        onChange={(e) =>
                          setXmlFile(e.target.files?.[0] || null)
                        }
                      />
                    </div>
                    <Button
                      variant="primary"
                      onClick={handleUploadInvoice}
                      disabled={!pdfFile || !xmlFile || uploadingInvoice}
                    >
                      {uploadingInvoice ? "Subiendo..." : "Subir factura"}
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ShowTicket;
